import { APP_NAME } from '@/constants'
import type { EmailGreetingBreakpoints } from '@features/settings/stores/useSettingsStore'
import { getEmailGreeting } from '@features/settings/utils/emailGreeting'

/** What a share sends: the link and the words around it. */
export interface SharePayload {
  subject: string
  message: string
  url: string
}

const EMAIL_LINE_BREAK = '\r\n'

export function trimLineEnd(value: string) {
  return value.replace(/[^\S\r\n]+$/u, '')
}

/** The full link to an address in the app. */
export function shareUrl(path: string) {
  return new URL(path, window.location.origin).toString()
}

/** A grid's view: the link opens its table on that view. */
export function viewSharePayload(url: string, tableName: string, viewName: string): SharePayload {
  const table = tableName.trim() || 'Table'
  const view = viewName.trim() || 'All Records'
  return {
    subject: trimLineEnd(`${APP_NAME}: "${view}" view from the "${table}" table`),
    message: trimLineEnd(
      `Here's a link to the "${view}" view from the "${table}" table in ${APP_NAME}:`
    ),
    url: trimLineEnd(url),
  }
}

/**
 * A page, by its name as the app names an address (`Automations › Runs ›
 * All Runs`, `Settings › Modules`); see describeSharedPage.
 */
export function pageSharePayload(url: string, pageName: string): SharePayload {
  const name = pageName.trim()
  return {
    subject: trimLineEnd(name ? `${APP_NAME}: ${name}` : APP_NAME),
    message: trimLineEnd(
      name ? `Here's a link to ${name} in ${APP_NAME}:` : `Here's a link to a page in ${APP_NAME}:`
    ),
    url: trimLineEnd(url),
  }
}

/** The email's body: the user's greeting and sign-off around the message and link. */
export function buildEmailBody(
  payload: SharePayload,
  emailGreetingBreakpoints: EmailGreetingBreakpoints,
  defaultEmailSignOff: string,
  now = new Date()
) {
  const lines = [
    getEmailGreeting(now, emailGreetingBreakpoints),
    '',
    payload.message,
    '',
    payload.url,
    '',
    defaultEmailSignOff,
  ].map(trimLineEnd)

  return `${lines.join(EMAIL_LINE_BREAK)}${EMAIL_LINE_BREAK}${EMAIL_LINE_BREAK}`
}
